/**
 * Policy routes. Counsel's legal/*.md pages render through LegalDocument.
 * /subprocessors is the Subprocessors page.
 *
 * POLICY_EFFECTIVE_DATE stays October 13, 2026 unless counsel changes it.
 * POLICY_LAST_UPDATED is the one line to change at go-live.
 */
import { PACARION_NO_VENDOR_PAYMENT, VENDOR_VETTING_NOTICE } from '../shared/vendorConsent';
import { scrollToLocationHash } from './appScroll';
export const POLICY_EFFECTIVE_DATE = 'October 13, 2026';
export const POLICY_LAST_UPDATED = 'October 22, 2026';

export const FIRST_PAID_REFUND_NOTICE =
  'You can request a full refund of your first paid charge within 7 days of that charge. Email support@pacarion.com with the subject "Refund". That refund also cancels the account. After those 7 days you can cancel anytime and keep access through the end of the billing period. We do not give prorated or partial-month refunds, except where required by law.';

export const COMPANY = {
  legalName: 'Pacarion LLC',
  address: 'PO Box 530213, DeBary, FL 32753',
  email: 'support@pacarion.com',
};

export const POLICY_PAGES = [
  { path: '/terms', key: 'terms', label: 'Terms', title: 'Terms of Service' },
  { path: '/privacy', key: 'privacy', label: 'Privacy', title: 'Privacy Policy' },
  { path: '/refunds', key: 'refund', label: 'Refunds', title: 'Refund & Cancellation Policy' },
  { path: '/refund', key: 'refund', label: 'Refunds', title: 'Refund & Cancellation Policy', alias: true },
  { path: '/trial-terms', key: 'trial', label: 'Trial Terms', title: 'Free Trial & Automatic Renewal Terms' },
  { path: '/cookies', key: 'cookies', label: 'Cookies', title: 'Cookie Policy' },
  { path: '/dpa', key: 'dpa', label: 'DPA', title: 'Data Processing Addendum' },
  { path: '/disclaimers', key: 'disclaimers', label: 'Disclaimers', title: 'Disclaimers' },
  { path: '/accessibility', key: 'accessibility', label: 'Accessibility', title: 'Accessibility' },
  { path: '/dmca', key: 'dmca', label: 'DMCA', title: 'Copyright (DMCA)' },
  { path: '/delete-account', key: 'data-deletion', label: 'Delete Account', title: 'Delete Account' },
  { path: '/data-deletion', key: 'data-deletion', label: 'Delete Account', title: 'Delete Account', alias: true },
  { path: '/subprocessors', key: 'subprocessors', label: 'Subprocessors', title: 'Subprocessors' },
];

export const FOOTER_LINKS = POLICY_PAGES.filter((page) => !page.alias);

export const LEGAL_INDEX_PATH = '/legal';

export const COOKIE_NOTICE =
  "Pacarion uses only essential cookies and similar storage to keep you signed in and secure. We don't use advertising or tracking cookies.";

export function legalPageFromPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return null;
  const path = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  if (path === LEGAL_INDEX_PATH) {
    return { path: LEGAL_INDEX_PATH, key: 'index', label: 'Legal', title: 'Legal' };
  }
  return POLICY_PAGES.find((page) => page.path === path) || null;
}

/** Set on history entries this app pushed. Browser Back and the on-page Back both use it. */
export const LEGAL_ENTRY_STATE = 'pacarionLegalEntry';

let legalAudience = { signedIn: false };

export function setLegalAudience(next) {
  legalAudience = { signedIn: Boolean(next && next.signedIn) };
}

export function readLegalAudience() {
  return legalAudience;
}

/**
 * In-app history goes back. A policy opened directly falls back to the legal
 * list. The legal list falls back to Settings when someone is signed in, and
 * to the login screen when they are not.
 */
export function legalBackAction({ pathname, historyState, signedIn, historyLength } = {}) {
  const inApp = Boolean(historyState && historyState[LEGAL_ENTRY_STATE]);
  if (inApp && Number(historyLength) > 1) return { type: 'back' };
  const page = legalPageFromPath(pathname);
  if (page && page.key !== 'index') return { type: 'replace', href: LEGAL_INDEX_PATH };
  if (signedIn) return { type: 'replace', href: '/?section=settings' };
  return { type: 'replace', href: '/login' };
}

export function openLegalPath(href) {
  const page = legalPageFromPath(String(href || '').split('?')[0]);
  if (!page || typeof window === 'undefined' || !window.history) return false;
  const hashAt = String(href || '').indexOf('#');
  const hash = hashAt >= 0 ? String(href).slice(hashAt) : '';
  const next = `${page.path}${hash}`;
  if (`${window.location.pathname}${window.location.hash}` !== next) {
    window.history.pushState({ [LEGAL_ENTRY_STATE]: true }, '', next);
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
  scrollToLocationHash();
  return true;
}

export function performLegalBack(history = typeof window !== 'undefined' ? window.history : null) {
  if (!history || typeof window === 'undefined') return null;
  const action = legalBackAction({
    pathname: window.location.pathname,
    historyState: history.state,
    signedIn: legalAudience.signedIn,
    historyLength: history.length,
  });
  if (action.type === 'back') {
    history.back();
    return action;
  }
  history.replaceState({}, '', action.href);
  window.dispatchEvent(new PopStateEvent('popstate'));
  return action;
}

export function followPolicyLink(event) {
  if (!event || event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
  const anchor = event.currentTarget;
  const href = anchor && anchor.getAttribute ? anchor.getAttribute('href') : '';
  if (!legalPageFromPath(href)) return;
  event.preventDefault();
  openLegalPath(href);
}

export const VENDOR_LISTING_NOTICE = PACARION_NO_VENDOR_PAYMENT;

export function vendorListIntro() {
  return PACARION_NO_VENDOR_PAYMENT;
}

export function vendorCardFootnote() {
  return VENDOR_VETTING_NOTICE;
}
